import type { SceneNode } from "@/document/model/nodes";
import { createDefaultStyle, normalizeStrokeStack, styleWithStrokeStack, type NodeEffect, type NodeStyle, type Paint } from "@/document/model/styles";
import type { RichTextModel, TypeStyle } from "@/document/model/text";
import type { VectorNetwork } from "@/document/model/vectorNetwork";
import { contoursToPathData, pathDataToContours } from "@/document/operations/pathGeometry";
import { createId } from "@/lib/ids";
import type { Rect } from "@/lib/math/rect";

export interface CloneSceneNodeOptions {
  offset?: { x: number; y: number };
  renameRoot?: boolean;
  instantiateComponent?: boolean;
}

interface CloneState {
  nodeIds: Map<string, string>;
  vertexIds: Map<string, string>;
}

export function cloneSceneNodeWithFreshIds(node: SceneNode, options: CloneSceneNodeOptions = {}): SceneNode {
  return cloneNode(node, options, { nodeIds: new Map(), vertexIds: new Map() }, true);
}

function cloneNode(node: SceneNode, options: CloneSceneNodeOptions, state: CloneState, isRoot: boolean): SceneNode {
  const cloned = structuredClone(node) as SceneNode;
  if (isRoot && options.instantiateComponent && cloned.type === "component") {
    const variant = cloned.children[0];
    const instance = {
      ...cloned,
      type: "instance" as const,
      id: createId("instance"),
      name: `${cloned.name} Instance`,
      style: createDefaultStyle("#000000"),
      bounds: variant?.bounds ? { ...variant.bounds } : { ...cloned.bounds },
      componentId: node.id,
      variantId: variant?.id ?? "",
      overrides: {},
      children: variant?.children ? structuredClone(variant.children) : [],
    };
    for (const child of instance.children) {
      assignComponentSourceIds(child);
      remapNodeIds(child, state, false);
    }
    offsetNodeGeometry(instance, options.offset ?? { x: 0, y: 0 });
    return instance;
  }

  remapNodeIds(cloned, state, isRoot && options.renameRoot === true);
  offsetNodeGeometry(cloned, options.offset ?? { x: 0, y: 0 });
  return cloned;
}

function assignComponentSourceIds(node: SceneNode): void {
  node.componentSourceId = node.id;
  const children = node.type === "frame" || node.type === "group" || node.type === "component" || node.type === "componentVariant" || node.type === "instance"
    ? node.children
    : node.type === "boolean"
      ? node.sourceNodes
      : undefined;
  if (children) {
    for (const child of children) {
      assignComponentSourceIds(child);
    }
  }
}

function remapNodeIds(node: SceneNode, state: CloneState, rename: boolean): void {
  const previousId = node.id;
  node.id = nextNodeId(node.type);
  state.nodeIds.set(previousId, node.id);
  if (rename) {
    node.name = copyName(node.name);
  }

  node.style = remapStyle(node.style);

  if (node.type === "text") {
    node.text = remapText(node.text);
  }

  if (node.type === "vectorNetwork") {
    node.network = remapVectorNetwork(node.network, state);
  }

  if (node.type === "frame" || node.type === "group" || node.type === "component" || node.type === "componentVariant" || node.type === "instance") {
    if (node.type === "frame" && node.guides?.length) {
      node.guides = node.guides.map(guide => ({ ...guide, id: createId("guide") }));
    }
    for (const child of node.children) {
      remapNodeIds(child, state, false);
    }
  }

  if (node.type === "boolean") {
    for (const child of node.sourceNodes) {
      remapNodeIds(child, state, false);
    }
    if (node.sourceGeometry) {
      node.sourceGeometry = node.sourceGeometry.map(source => ({
        ...source,
        nodeId: state.nodeIds.get(source.nodeId) ?? source.nodeId,
      }));
    }
  }
}

function remapVectorNetwork(network: VectorNetwork, state: CloneState): VectorNetwork {
  const vertexIds = new Map(network.vertices.map(vertex => [vertex.id, createId("vertex")]));
  for (const [from, to] of vertexIds) {
    state.vertexIds.set(from, to);
  }

  const edgeIds = new Map<string, string>();
  const edges = network.edges.flatMap(edge => {
    const fromVertexId = vertexIds.get(edge.from.vertexId);
    const toVertexId = vertexIds.get(edge.to.vertexId);
    if (!fromVertexId || !toVertexId) {
      return [];
    }

    const nextEdgeId = createId("edge");
    edgeIds.set(edge.id, nextEdgeId);
    return [{
      ...edge,
      id: nextEdgeId,
      from: {
        ...edge.from,
        vertexId: fromVertexId,
        handle: edge.from.handle ? { ...edge.from.handle } : undefined,
      },
      to: {
        ...edge.to,
        vertexId: toVertexId,
        handle: edge.to.handle ? { ...edge.to.handle } : undefined,
      },
    }];
  });

  const regions = network.regions.flatMap(region => {
    const remappedEdgeIds = region.edgeIds.flatMap(edgeId => {
      const remappedEdgeId = edgeIds.get(edgeId);
      return remappedEdgeId ? [remappedEdgeId] : [];
    });
    if (remappedEdgeIds.length !== region.edgeIds.length) {
      return [];
    }

    return [{
      ...region,
      edgeIds: remappedEdgeIds,
    }];
  });

  return {
    ...network,
    vertices: network.vertices.map(vertex => ({
      ...vertex,
      id: vertexIds.get(vertex.id)!,
    })),
    edges,
    regions,
    fillOverrideTable: remapFillOverrideTable(network.fillOverrideTable, edgeIds),
  };
}

function remapFillOverrideTable(
  table: VectorNetwork["fillOverrideTable"],
  edgeIds: Map<string, string>,
): VectorNetwork["fillOverrideTable"] {
  if (!table) {
    return undefined;
  }

  const remapped: NonNullable<VectorNetwork["fillOverrideTable"]> = {};
  for (const [key, value] of Object.entries(table)) {
    const nextKey = remapRegionKey(key, edgeIds);
    if (nextKey) {
      remapped[nextKey] = structuredClone(value);
    }
  }

  return Object.keys(remapped).length > 0 ? remapped : undefined;
}

function remapRegionKey(key: string, edgeIds: Map<string, string>): string | undefined {
  const parts = key.split(":");
  const remapped = parts.map(part => edgeIds.get(part));
  if (remapped.some(part => !part)) {
    return undefined;
  }

  return remapped.join(":");
}

function remapText(text: RichTextModel): RichTextModel {
  return {
    ...text,
    style: remapTypeStyle(text.style),
    styleOverrideTable: Object.fromEntries(
      Object.entries(text.styleOverrideTable).map(([id, style]) => [id, remapTypeStyle(style)]),
    ),
  };
}

function remapTypeStyle<T extends Partial<TypeStyle>>(style: T): T {
  return {
    ...style,
    fill: style.fill ? remapPaint(style.fill) : undefined,
  };
}

function remapStyle(style: NodeStyle): NodeStyle {
  return styleWithStrokeStack({
    ...style,
    fills: style.fills.map(remapPaint),
    effects: style.effects?.map(remapEffect),
  }, normalizeStrokeStack(style).map(stroke => ({ ...stroke, paint: remapPaint(stroke.paint) })));
}

function remapPaint(paint: Paint): Paint {
  if (paint.type === "solid") {
    return {
      ...structuredClone(paint),
      id: createId("paint"),
    };
  }

  if (paint.type === "image") {
    return {
      ...structuredClone(paint),
      id: createId("paint"),
    };
  }

  return {
    ...paint,
    id: createId("paint"),
    stops: paint.stops.map(stop => ({ ...stop, id: createId("stop") })),
  };
}

function remapEffect(effect: NodeEffect): NodeEffect {
  return {
    ...effect,
    id: createId("effect"),
  };
}

function offsetNodeGeometry(node: SceneNode, offset: { x: number; y: number }): void {
  if (offset.x === 0 && offset.y === 0) {
    return;
  }

  node.bounds = offsetRect(node.bounds, offset);
  offsetNodeRotationOrigin(node, offset);

  if (node.type === "shape") {
    node.path = offsetPath(node.path, offset);
  }

  if (node.type === "vectorNetwork") {
    offsetVectorNetwork(node.network, offset);
  }

  if (node.type === "frame" || node.type === "group" || node.type === "component" || node.type === "componentVariant" || node.type === "instance") {
    for (const child of node.children) {
      offsetNodeGeometry(child, offset);
    }
  }

  if (node.type === "boolean") {
    node.resolved = {
      contours: offsetContours(node.resolved.contours, offset),
      path: offsetPath(node.resolved.path, offset),
    };
    node.sourceGeometry = node.sourceGeometry?.map(source => ({
      ...source,
      sourceBounds: source.sourceBounds ? offsetRect(source.sourceBounds, offset) : undefined,
      bounds: offsetRect(source.bounds, offset),
      contours: offsetContours(source.contours, offset),
      path: source.path ? offsetPath(source.path, offset) : undefined,
    }));
    for (const child of node.sourceNodes) {
      offsetNodeGeometry(child, offset);
    }
  }
}

function offsetNodeRotationOrigin(node: SceneNode, offset: { x: number; y: number }): void {
  if (!Number.isFinite(node.transform.rotation) || node.transform.rotation === 0) {
    return;
  }

  if (!Number.isFinite(node.transform.x) || !Number.isFinite(node.transform.y)) {
    return;
  }

  if (node.transform.x === 0 && node.transform.y === 0) {
    return;
  }

  node.transform = {
    ...node.transform,
    x: node.transform.x + offset.x,
    y: node.transform.y + offset.y,
  };
}

function offsetVectorNetwork(network: VectorNetwork, offset: { x: number; y: number }): void {
  for (const vertex of network.vertices) {
    vertex.x += offset.x;
    vertex.y += offset.y;
  }

  for (const edge of network.edges) {
    if (edge.from.handle) {
      edge.from.handle = offsetPoint(edge.from.handle, offset);
    }
    if (edge.to.handle) {
      edge.to.handle = offsetPoint(edge.to.handle, offset);
    }
  }
}

function offsetPath(path: string, offset: { x: number; y: number }): string {
  return contoursToPathData(offsetContours(pathDataToContours(path), offset));
}

function offsetContours(contours: Array<Array<{ x: number; y: number }>>, offset: { x: number; y: number }): Array<Array<{ x: number; y: number }>> {
  return contours.map(contour => contour.map(point => offsetPoint(point, offset)));
}

function offsetPoint(point: { x: number; y: number }, offset: { x: number; y: number }): { x: number; y: number } {
  return {
    x: point.x + offset.x,
    y: point.y + offset.y,
  };
}

function offsetRect(rect: Rect, offset: { x: number; y: number }): Rect {
  return {
    ...rect,
    x: rect.x + offset.x,
    y: rect.y + offset.y,
  };
}

function nextNodeId(type: SceneNode["type"]): string {
  if (type === "frame" || type === "text" || type === "group" || type === "component" || type === "componentVariant" || type === "instance") {
    return createId(type);
  }

  return createId("node");
}

function copyName(name: string): string {
  return /\bcopy\b/i.test(name) ? name : `${name} Copy`;
}
